import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    fetchEnrollmentAndEnrolleeDetails,
    setRequestResponse,
} from '@actions/memberPortal/enrollmentsActionCreators';
import { useModal } from '@hooks/index';
import useGetSelectedEnrollmentDetails from '../../hooks/useGetSelectedEnrollmentDetails';

import useSetSelectedEnrollment from '@hooks/useSetSelectedEnrollment';
import {
    IEnrollment,
    IEnrollmentModalProps,
    IEnrollmentOverrideModal,
    IEnrollmentPremiumHistoryModal,
    IEnrollmentReinstateModal,
    IEnrollmentResendModal,
    IEnrollmentSnapshotResponse,
    IMemberResendRequest,
    IOverrideEnrollmentStatusRequest,
    IPremiumHistoryEnrollmentRequest,
    IReinstateEnrollmentRequest,
    IResultModal,
} from '../common/types';
import { MODAL_KEYS } from '../modals/modalKeys';
import useEnrollmentModalAPI from './useEnrollmentModalAPI';

const useEnrollmentModal = () => {
    const { id } = useParams();
    const { openModal } = useModal();
    const intl = useIntl();
    const dispatch = useDispatch();
    const getSelectedEnrollmentDetails = useGetSelectedEnrollmentDetails();
    const setSelectedEnrollment = useSetSelectedEnrollment();
    const { setEnrollmentResend, setReinstateStatus, setEnrollmentStatus, setCancelActiveEnrollment } =
        useEnrollmentModalAPI();

    const clearState = () => {
        dispatch(setRequestResponse({ isSuccess: false, isError: false, errorMessage: '' }));
    };

    const handleOpenModal = (modalType: string, params?: any) => {
        openModal(modalType, params);
    };

    const getEnrollmentDetails = () => {
        //on success refetch the enrollment details
        const formData = new FormData();
        formData.append('encHouseholdId', id);
        formData.append('csrftoken', window.csrf_token);
        dispatch(fetchEnrollmentAndEnrolleeDetails(formData));
    };

    const handleResend = async (payload: IMemberResendRequest) => {
        const result = await setEnrollmentResend(payload);
        if (result?.isSuccess) {
            getEnrollmentDetails();
        }
    };

    const handleOverrideEnrollment = async (payload: IOverrideEnrollmentStatusRequest, status) => {
        const result = await setEnrollmentStatus(payload, status);  
        if (result?.isSuccess) {
            getEnrollmentDetails();
        }
    };

    const handleReinstateEnrollment = async (payload: IReinstateEnrollmentRequest) => {
        const result = await setReinstateStatus(payload);
        if (result?.isSuccess) {
            getEnrollmentDetails();
        }
    };

    const handlePremiumHistoryEnrollment = async (payload: IPremiumHistoryEnrollmentRequest) => {
        const result = await setCancelActiveEnrollment(payload);
        if (result?.isSuccess) {
            getEnrollmentDetails();
        }
    };

    const reloadEnrollmentDetails = async eId => {
        const formData = new FormData();
        formData.append('encHouseholdId', id);
        formData.append('csrftoken', window.csrf_token);
        const enrollmentDetails = await dispatch(fetchEnrollmentAndEnrolleeDetails(formData));
        if (enrollmentDetails) {
            const enrollment = await getSelectedEnrollmentDetails(eId, enrollmentDetails);
            setSelectedEnrollment(enrollment);
        }
    };

    const modalProps: IEnrollmentModalProps = useMemo(
        () => ({
            resendHistory: {
                handleSubmit: handleResend,
                content: {
                    title: intl.formatMessage({ id: 'member.enrollment.modal.resendHistory.title' }),
                    body: intl.formatMessage({ id: 'member.enrollment.modal.resendHistory.body' }),
                    eventType: 'ALL',
                },
                successMessage: intl.formatMessage({ id: 'member.enrollment.modal.resend.Message' }),
                errorMessage: intl.formatMessage({ id: 'member.enrollment.modal.resend.Message' }),
                clearState: clearState,
            },
            resendTransactions: {
                handleSubmit: handleResend,
                content: {
                    title: intl.formatMessage({ id: 'member.enrollment.modal.resendTranscation.title' }),
                    body: intl.formatMessage({ id: 'member.enrollment.modal.resendTranscation.body' }),
                    eventType: 'LATEST',
                },
                successMessage: intl.formatMessage({ id: 'member.enrollment.modal.resend.Message' }),
                errorMessage: intl.formatMessage({ id: 'member.enrollment.modal.resend.Message' }),
                clearState: clearState,
            },
            overrideEnrollment: {
                handleSubmit: handleOverrideEnrollment,
                content: {
                    title: intl.formatMessage({ id: 'member.enrollment.modal.overrideEnrollmentStatus.title' }),
                    body: intl.formatMessage({ id: 'member.enrollment.modal.overrideEnrollmentStatus.body' }),
                    eventType: 'OVERRIDE',
                },

                successMessage: intl.formatMessage({ id: 'member.enrollment.modal.overrideEnrollmentStatus.Message' }),
                errorMessage: [],
                clearState: clearState,
            },
            reinstateenrollment: {
                handleSubmit: handleReinstateEnrollment,
                content: {
                    title: intl.formatMessage({ id: 'member.enrollment.modal.reinstate.title' }),
                    body: intl.formatMessage({ id: 'member.enrollment.modal.reinstate.body' }),
                    eventType: 'REINSTATE',
                },
                successMessage: intl.formatMessage({ id: 'member.enrollment.modal.reinstate.Message' }),
                errorMessage: [
                    intl.formatMessage({ id: 'member.enrollment.modal.reinstate.errorMessage' }),
                    intl.formatMessage({ id: 'member.enrollment.modal.reinstate.errorMessage2' }),
                ],
                clearState: clearState,
            },
            premiumHistory: {
                handleSubmit: handlePremiumHistoryEnrollment,
                content: {
                    title: intl.formatMessage({ id: 'member.enrollment.modal.premiumHistoryModal.title' }),
                    body: '',
                    eventType: 'CANCEL',
                },
                successMessage: intl.formatMessage({ id: 'member.enrollment.modal.cancelPremiumHistory.Message' }),
                errorMessage: intl.formatMessage({ id: 'member.enrollment.modal.cancelPremiumHistory.errorMessage' }),
                clearState: clearState,
            },
        }),
        [],
    );

    return {
        handleCancelTerminate: (
            enrollmentShanpshot: IEnrollmentSnapshotResponse,
            premiumHistory: IEnrollmentPremiumHistoryModal,
        ) => handleOpenModal(MODAL_KEYS.CANCEL_TERMINATE, { enrollmentShanpshot, premiumHistory }),

        handleOverrideEnrollmentStatus: (enrollment: IEnrollment, overrideEnrollment: IEnrollmentOverrideModal) => 
            handleOpenModal(MODAL_KEYS.OVERRIDE_ENROLLMENT_STATUS, { enrollment, overrideEnrollment }),

        handlePremiumHistory: (enrollment: IEnrollment, premiumHistory: IEnrollmentPremiumHistoryModal) =>
            handleOpenModal(MODAL_KEYS.PREMIUM_HISTORY, { enrollment, premiumHistory }),

        handleResendTransactions: (encEnrollmentId: string, resend: IEnrollmentResendModal) =>
            handleOpenModal(MODAL_KEYS.RESEND_TRANSACTIONS, { encEnrollmentId, resend }),

        handleResendHistory: (encEnrollmentId: string, resend: IEnrollmentResendModal) =>
            handleOpenModal(MODAL_KEYS.RESEND_TRANSACTIONS, { encEnrollmentId, resend }),

        handleReinstateEnrollment: (enrollment: IEnrollment, reinstateenrollment: IEnrollmentReinstateModal) =>
            handleOpenModal(MODAL_KEYS.REINSTATE_ENROLLMENT, { enrollment, reinstateenrollment }),

        handleShowResult: (result: IResultModal) => handleOpenModal(MODAL_KEYS.RESULT, { result, clearState }),

        reloadEnrollmentDetails,

        ...modalProps,
    };
};

export default useEnrollmentModal;
